import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  ArrowLeft,
  Search,
  Workflow,
  ChevronRight
} from 'lucide-react';
import ProcessService from './services/ProcessService';


// Composant Architecture
const IntegrationArchitecture = () => (
  <svg className="w-full" viewBox="0 0 1200 500">
    <defs>
      <linearGradient id="architectureGradient" x1="0%" y1="0%" x2="100%" y2="0%">
        <stop offset="0%" stopColor="#017D7E" stopOpacity="0.2"/>
        <stop offset="100%" stopColor="#00A99D" stopOpacity="0.2"/>
      </linearGradient>
      <filter id="shadow" x="-20%" y="-20%" width="140%" height="140%">
        <feGaussianBlur in="SourceAlpha" stdDeviation="3"/>
        <feOffset dx="2" dy="2"/>
        <feComponentTransfer>
          <feFuncA type="linear" slope="0.3"/>
        </feComponentTransfer>
        <feMerge>
          <feMergeNode/>
          <feMergeNode in="SourceGraphic"/>
        </feMerge>
      </filter>
    </defs>

    {/* External Systems */}
    <g transform="translate(50, 50)">
      {/* SYSTAC1 */}
      <g filter="url(#shadow)">
        <rect
          x="20"
          y="20"
          width="200"
          height="100"
          fill="#0F172A"
          stroke="#017D7E"
          strokeWidth="2"
          rx="8"
        />
        <text
          x="120"
          y="65"
          fill="white"
          fontSize="18"
          fontWeight="bold"
          textAnchor="middle"
        >
          SYSTAC1
        </text>
        <text
          x="120"
          y="90"
          fill="white"
          fontSize="14"
          textAnchor="middle"
          opacity="0.7"
        >
          Legacy System
        </text>
      </g>

      {/* MXPAY Core */}
      <g transform="translate(300, 0)">
        <rect
          width="500"
          height="400"
          fill="url(#architectureGradient)"
          stroke="#017D7E"
          strokeWidth="2"
          rx="12"
          filter="url(#shadow)"
        />
        
        <text
          x="250"
          y="50"
          fill="white"
          fontSize="24"
          fontWeight="bold"
          textAnchor="middle"
        >
          MXPAY Integration Layer
        </text>

        {/* Components */}
        {[
          ["Message Processor", "Format Converter", "Routing Engine"],
          ["Validation Service", "Business Rules", "Monitoring"],
          ["Security Layer", "Audit Service", "Reporting"]
        ].map((row, i) => 
          row.map((service, j) => (
            <g key={`${i}-${j}`} transform={`translate(${50 + j * 150}, ${100 + i * 80})`}>
              <rect
                width="130"
                height="50"
                fill="#0F766E"
                stroke="#017D7E"
                strokeWidth="1"
                rx="6"
              />
              <text
                x="65"
                y="30"
                fill="white"
                fontSize="12"
                textAnchor="middle"
              >
                {service}
              </text>
            </g>
          ))
        )}

        {/* Infrastructure Layer */}
        <g transform="translate(50, 340)">
          <rect
            width="400"
            height="40"
            fill="#1E4976"
            stroke="#017D7E"
            strokeWidth="1"
            rx="6"
          />
          <text
            x="200"
            y="25"
            fill="white"
            fontSize="12"
            textAnchor="middle"
          >
            Event Store | Message Queue | Cache | Monitoring
          </text>
        </g>
      </g>

      {/* PS-ACH */}
      <g transform="translate(880, 20)" filter="url(#shadow)">
        <rect
          width="200"
          height="100"
          fill="#0F172A"
          stroke="#017D7E"
          strokeWidth="2"
          rx="8"
        />
        <text
          x="100"
          y="45"
          fill="white"
          fontSize="18"
          fontWeight="bold"
          textAnchor="middle"
        >
          PS-ACH
        </text>
        <text
          x="100"
          y="70"
          fill="white"
          fontSize="14"
          textAnchor="middle"
          opacity="0.7"
        >
          Payment System
        </text>
      </g>

      {/* Connections */}
      <path
        d="M220 70 L300 70"
        stroke="#017D7E"
        strokeWidth="2"
        strokeDasharray="4"
        markerEnd="url(#arrowhead)"
      />
      <path
        d="M800 70 L880 70"
        stroke="#017D7E"
        strokeWidth="2"
        strokeDasharray="4"
        markerEnd="url(#arrowhead)"
      />
    </g>

    {/* Arrow Marker */}
    <defs>
      <marker
        id="arrowhead"
        markerWidth="10"
        markerHeight="7"
        refX="9"
        refY="3.5"
        orient="auto"
      >
        <polygon
          points="0 0, 10 3.5, 0 7"
          fill="#017D7E"
        />
      </marker>
    </defs>
  </svg>
);

// Composants UI de base
const Card = ({ children, className = '', onClick }) => (
  <div 
    onClick={onClick}
    className={`bg-white/5 backdrop-blur-sm rounded-lg border border-white/10 hover:border-[#017D7E]/50 transition-all ${className}`}
  >
    {children}
  </div>
);

const CardContent = ({ children, className = '' }) => (
  <div className={`px-6 py-4 ${className}`}>
    {children}
  </div>
);

// Composant de présentation SYSTAC2
const SYSTAC2Overview = () => (
  <>
    <Card className="mb-8">
      <CardContent>
        <div className="prose prose-invert max-w-none">
          <h2 className="text-2xl font-bold mb-4 text-white">SYSTAC2 avec MXPAY</h2>
          <p className="text-white/90">
            MXPAY s'intègre nativement avec SYSTAC2, le système de télé-compensation
            de la CEMAC, offrant une solution complète pour la gestion des opérations
            de paiement régionales.
          </p>
          
          <h3 className="text-xl font-bold mt-6 mb-4 text-white">Fonctionnalités Clés</h3>
          <ul className="list-disc pl-4 space-y-2">
            <li className="text-white/90">Support complet des formats SYSTAC2</li>
            <li className="text-white/90">Gestion automatisée des sessions de compensation</li>
            <li className="text-white/90">Suivi temps réel des opérations</li>
            <li className="text-white/90">Reporting réglementaire intégré</li>
          </ul>

          <h3 className="text-xl font-bold mt-6 mb-4 text-white">Avantages</h3>
          <ul className="list-disc pl-4 space-y-2">
            <li className="text-white/90">Réduction des coûts opérationnels</li>
            <li className="text-white/90">Amélioration de la conformité</li>
            <li className="text-white/90">Optimisation du traitement des opérations</li>
            <li className="text-white/90">Support technique 24/7</li>
          </ul>
        </div>
      </CardContent>
    </Card>
    
    <Card className="mb-8">
      <CardContent>
        <h2 className="text-2xl font-bold mb-6 text-white">Architecture d'Intégration</h2>
        <IntegrationArchitecture />
      </CardContent>
    </Card>
  </>
);

// Composant de recherche
const ProcessSearch = ({ searchTerm, onSearchChange }) => (
  <div className="relative mb-6">
    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-white/40" />
    <input
      type="text"
      placeholder="Rechercher un processus..."
      value={searchTerm}
      onChange={(e) => onSearchChange(e.target.value)}
      className="w-full pl-10 pr-4 py-2 bg-white/5 border border-white/10 rounded-lg
               text-white placeholder-white/40 focus:border-[#017D7E] transition-colors"
    />
  </div>
);

// Page principale SYSTAC2
const SYSTAC2Page = () => {
  const navigate = useNavigate();
  const [processes, setProcesses] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadProcesses = async () => {
      try {
        setLoading(true);
        const data = await ProcessService.loadAllProcessFiles();
        setProcesses(data.processes);
      } catch (error) {
        console.error('Error loading processes:', error);
      } finally {
        setLoading(false);
      }
    };

    loadProcesses();
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#017D7E]"></div>
      </div>
    );
  }

  // Filtrer les processus en fonction du terme de recherche
  const filteredProcesses = processes.filter(process => {
    if (!searchTerm) return true;
    
    return (
      process.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      process.description?.toLowerCase().includes(searchTerm.toLowerCase())
    );
  });

  return (
    <div className="min-h-screen p-8">
      {/* Background avec gradient */}
      <div className="fixed inset-0 -z-10 bg-[#050A1C]">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_top,rgba(1,125,126,0.15),transparent_50%)]" />
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_center_left,rgba(0,169,157,0.15),transparent_50%)]" />
      </div>

      <div className="max-w-6xl mx-auto">
        {/* Navigation */}
        <button
          onClick={() => navigate(-1)}
          className="mb-8 flex items-center gap-2 text-white/70 hover:text-white group"
        >
          <ArrowLeft className="w-5 h-5 group-hover:-translate-x-1 transition-transform" />
          Retour
        </button>

        {/* Présentation SYSTAC2 */}
        <SYSTAC2Overview />

        {/* Recherche */}
        <ProcessSearch
          searchTerm={searchTerm}
          onSearchChange={setSearchTerm}
        />

        {/* Liste des processus */}
        <div className="grid grid-cols-1 gap-6">
          {filteredProcesses.map((process) => (
            <Card 
              key={process.id}
              onClick={() => navigate(`process/${process.id}`)}
              className="cursor-pointer"
            >
              <CardContent>
                <div className="flex items-start justify-between">
                  <div className="flex items-start gap-4">
                    <div className="p-2 rounded-lg bg-[#017D7E]/10">
                      <Workflow className="w-6 h-6 text-[#017D7E]" />
                    </div>
                    <div>
                      <h3 className="text-xl font-bold mb-2 text-white">{process.name}</h3>
                      <p className="text-white/90">{process.description}</p>
                      
                      {process.tags && (
                        <div className="mt-4 flex flex-wrap gap-2">
                          {process.tags.map((tag, index) => (
                            <span
                              key={index}
                              className="px-2 py-1 text-sm bg-[#017D7E]/10 text-[#017D7E] rounded-full"
                            >
                              {tag}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                  
                  <ChevronRight className="w-5 h-5 text-white/40" />
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    </div>
  );
};

export default SYSTAC2Page;